/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Unsubscribe } from '@amazon/vinyl-util'

type ConnectionType = 'connect' | 'disconnect'

interface Entry {
    readonly listener: EventListenerOrEventListenerObject
    readonly capture: boolean
    readonly once: boolean
    removed: boolean
}

const LISTENERS = Symbol('connect and disconnect listeners')
const HOLDING = Symbol('holds connection listeners')

type Held = Node & { [LISTENERS]?: Partial<Record<ConnectionType, Entry[]>> }

function isConnectionType(type: string): type is ConnectionType {
    return type === 'connect' || type === 'disconnect'
}

function captures(options: boolean | EventListenerOptions | undefined): boolean {
    return typeof options === 'boolean' ? options : !!options?.capture
}

function find(node: Held, type: ConnectionType, listener: unknown, capture: boolean) {
    return node[LISTENERS]?.[type]?.find((e) => e.listener === listener && e.capture === capture)
}

/**
 * Takes over `connect` and `disconnect` listeners: instead of registering them with the DOM,
 * every node keeps its own, which {@link initializeConnectedObserver} calls directly. Dispatching
 * those events by hand reaches no one. Must run before the first `onConnect`; `app.ts` imports
 * this module first. Idempotent.
 */
export function holdConnectionListeners(): void {
    if (typeof Node === 'undefined' || HOLDING in Node.prototype) return
    const proto = Node.prototype
    const add = Reflect.get(proto, 'addEventListener') as typeof proto.addEventListener
    const remove = Reflect.get(proto, 'removeEventListener') as typeof proto.removeEventListener
    const define = (name: string, value: unknown) =>
        Object.defineProperty(proto, name, { configurable: true, writable: true, value })
    Object.defineProperty(proto, HOLDING, { value: true })
    define(
        'addEventListener',
        function addEventListener(this: Held, ...args: Parameters<typeof add>) {
            const [type, listener, options] = args
            if (!isConnectionType(type)) return add.apply(this, args)
            const capture = captures(options)
            if (!listener || find(this, type, listener, capture)) return
            const once = typeof options === 'object' && !!options.once
            const listeners = this[LISTENERS] ?? {}
            this[LISTENERS] = listeners
            ;(listeners[type] ??= []).push({ listener, capture, once, removed: false })
        }
    )
    define(
        'removeEventListener',
        function removeEventListener(this: Held, ...args: Parameters<typeof remove>) {
            const [type, listener, options] = args
            if (!isConnectionType(type)) return remove.apply(this, args)
            const entry = find(this, type, listener, captures(options))
            if (!entry) return
            entry.removed = true
            const entries = this[LISTENERS]![type]!
            entries.splice(entries.indexOf(entry), 1)
        }
    )
}

holdConnectionListeners()

function invoke(node: Node, entry: Entry, event: Event): void {
    try {
        if (typeof entry.listener === 'function') entry.listener.call(node, event)
        else entry.listener.handleEvent(event)
    } catch (error) {
        // As the DOM reports a listener's error: the rest of the batch still runs.
        setTimeout(() => {
            throw error
        })
    }
}

function deliverTo(type: ConnectionType): (node: Held) => void {
    const event = new CustomEvent(type)
    return (node) => {
        const entries = node[LISTENERS]?.[type]
        if (!entries?.length) return
        for (const entry of entries.slice()) {
            if (entry.removed) continue
            if (entry.once) node.removeEventListener(type, entry.listener, entry.capture)
            invoke(node, entry, event)
        }
    }
}

function walkDepthFirst(node: Node, callback: (node: Node) => void): void {
    let next = node.firstChild
    while (next) {
        const after = next.nextSibling
        walkDepthFirst(next, callback)
        next = after
    }
    callback(node)
}

/** Delivers `connect` and `disconnect` for a batch of mutation records, as vinyl-tsx does. */
export function deliverConnections(
    mutations: readonly Pick<MutationRecord, 'addedNodes' | 'removedNodes'>[]
): void {
    const removed = new Set<Node>()
    const added = new Set<Node>()
    for (const mutation of mutations) {
        mutation.removedNodes.forEach((node) => removed.add(node))
        mutation.addedNodes.forEach((node) => added.add(node))
    }
    const connect = deliverTo('connect')
    const disconnect = deliverTo('disconnect')
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (!removed.has(node)) walkDepthFirst(node, connect)
        }
        for (const node of mutation.removedNodes) {
            if (!added.has(node)) walkDepthFirst(node, disconnect)
        }
    }
}

/**
 * vinyl-tsx's connected observer, calling the listeners it holds directly. Vinyl's dispatches an
 * event to every node of every added and removed subtree, and on a page swap that dispatch, not
 * the listeners, was most of the task.
 */
export function initializeConnectedObserver(): Unsubscribe {
    holdConnectionListeners()
    const observer = new MutationObserver(deliverConnections)
    observer.observe(document, { childList: true, subtree: true })
    return () => observer.disconnect()
}
